import styled from "styled-components";
import {
  GlobalStyleState,
  ProductState,
  SectionState,
  SelectedFeatureMapState,
} from "../store/chart";
import { FontWeight, UIColor } from "../types";
import debounce from "lodash/debounce";
import {
  Comparison,
  ComparisonPlaceholder,
  ProductOverviewComparison,
} from "./Comparison";
import { Feature, FeatureName } from "./Feature";
import { MobileTableRow } from "./Table";
import { Text } from "./Text";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as CaretDownIcon } from "../assets/images/caret-down.svg";

type MobileFeatureSectionProps = {
  section: SectionState;
  selectedProductColumn1: ProductState | null;
  selectedProductColumn2: ProductState | null;
  selectedFeatureMap: SelectedFeatureMapState;
  legalCopyArray: string[];
  styling: GlobalStyleState;
  showOnlyDiffs?: boolean;
};

export function MobileFeatureSection({
  section,
  selectedProductColumn1,
  selectedProductColumn2,
  selectedFeatureMap,
  legalCopyArray,
  styling,
  showOnlyDiffs,
}: MobileFeatureSectionProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleResize = useCallback(() => {
    if (bodyRef.current) {
      const children = Array.from(bodyRef.current.children);
      const height = children.reduce(
        (acc, child) => acc + child.clientHeight,
        0,
      );
      setMaxHeight(height + 100);
    }
  }, []);

  const handleCollapseToggle = useCallback(() => {
    handleResize();
    setCollapsed((collapsed) => !collapsed);
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener("resize", debounce(handleResize, 300));
    window.addEventListener("deviceorientation", debounce(handleResize, 300));
    // handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("deviceorientation", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    setCollapsed(section.collapsed);
  }, [section.collapsed]);

  return (
    <div>
      <MobileFeatureSectionTitle
        font={section.font}
        textColor={section.mobileTextColor}
        backgroundColor={section.mobileBackgroundColor}
        onClick={handleCollapseToggle}
      >
        <span>{section.name}</span>
        <CollapsibleIcon
          collapsed={collapsed}
          color={section.mobileTextColor?.hex}
        >
          <CaretDownIcon />
        </CollapsibleIcon>
      </MobileFeatureSectionTitle>
      <MobileFeatureSectionBody
        ref={bodyRef}
        collapsed={collapsed}
        style={
          collapsed ? { maxHeight: 0 } : maxHeight !== null ? { maxHeight } : {}
        }
      >
        {section.subSections.map((subSection, index) => {
          const filteredFeatures = showOnlyDiffs
            ? subSection.features.filter(
                (feature) => selectedFeatureMap[feature.id].hasDifferences,
              )
            : subSection.features;
          const features = filteredFeatures.map((feature) => (
            <MobileFeatureRow key={feature.id}>
              <MobileFeatureColumn>
                <Feature feature={feature} legalCopyArray={legalCopyArray} />
              </MobileFeatureColumn>
              {selectedProductColumn1 && (
                <Comparison
                  product={selectedProductColumn1}
                  productFeatureMap={selectedFeatureMap[feature.id]}
                  legalCopyArray={legalCopyArray}
                  styling={styling}
                />
              )}
              {selectedProductColumn2 && (
                <Comparison
                  product={selectedProductColumn2}
                  productFeatureMap={selectedFeatureMap[feature.id]}
                  legalCopyArray={legalCopyArray}
                  styling={styling}
                />
              )}
            </MobileFeatureRow>
          ));
          let overview;
          if (
            index === 0 &&
            section.showProductOverview &&
            section.productOverviewTitle
          ) {
            overview = (
              <MobileFeatureRow>
                <MobileFeatureColumn>
                  <FeatureName>{section.productOverviewTitle}</FeatureName>
                </MobileFeatureColumn>
                {selectedProductColumn1 && (
                  <ComparisonPlaceholder
                    product={selectedProductColumn1}
                    styling={styling}
                  >
                    <ProductOverviewComparison
                      overview={selectedProductColumn1.overview}
                    />
                  </ComparisonPlaceholder>
                )}
                {selectedProductColumn2 && (
                  <ComparisonPlaceholder
                    product={selectedProductColumn2}
                    styling={styling}
                  >
                    <ProductOverviewComparison
                      overview={selectedProductColumn2.overview}
                    />
                  </ComparisonPlaceholder>
                )}
              </MobileFeatureRow>
            );
          }
          if (!subSection.name) {
            return overview ? (
              <>
                {overview}
                {features}
              </>
            ) : (
              features
            );
          }
          return (
            <MobileFeatureSubSection key={subSection.id}>
              {overview}
              <MobileFeatureSubSectionTitle
                font={subSection.font}
                textColor={subSection.mobileTextColor}
                backgroundColor={subSection.mobileBackgroundColor}
              >
                {subSection.name}
              </MobileFeatureSubSectionTitle>
              {features}
            </MobileFeatureSubSection>
          );
        })}
      </MobileFeatureSectionBody>
    </div>
  );
}

const MobileFeatureSubSection = styled.div`
  margin-top: 1px;
`;

const MobileFeatureSectionBody = styled.div<{ collapsed?: boolean }>`
  display: block;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  transition: all 0.5s;
`;

const MobileFeatureSectionTitle = styled(Text)<{ backgroundColor?: UIColor }>`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Black } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(22)}px;
    font-style: italic;
    line-height: 1;
    margin: 0 -${({ theme }) => theme.getValue(8)}px;
    padding: ${({ theme }) =>
      `${theme.getValue(12)}px ${theme.getValue(24)}px`};
    color: ${({ theme, textColor: { hex = theme.colors.white } = {} }) => hex};
    background: ${({ backgroundColor, theme }) =>
      backgroundColor?.hex || theme.colors.green6};
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const MobileFeatureSubSectionTitle = styled(MobileFeatureSectionTitle)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(16)}px;
    font-style: normal;
  }
`;

const MobileFeatureRow = styled(MobileTableRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.black};
`;

const MobileFeatureColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => `${theme.getValue(24)}px 0`};
`;

const CollapsibleIcon = styled.span<{ collapsed?: boolean; color?: string }>`
  position: relative;
  font-size: ${({ theme }) => theme.getFontSize(14)}px;
  line-height: 1;
  width: ${({ theme }) => theme.getValue(14)}px;
  height: ${({ theme }) => theme.getValue(14)}px;
  transition: transform 0.5s;
  transform: ${({ collapsed }) => `rotate(${collapsed ? "-90deg" : "0deg"})`};

  & > svg {
    font-size: ${({ theme }) => theme.getFontSize(14)}px;
    width: ${({ theme }) => theme.getFontSize(14)}px;
    height: ${({ theme }) => theme.getFontSize(14)}px;
    fill: ${({ color, theme }) => color || theme.colors.white};
  }
`;
